export interface SchemaField {
  name: string;
  dataType: string;
  fieldType: string;
  examples: string;
  comments?: string;
}

export const transactionSchema: SchemaField[] = [
    {
      name: 'transaction_id',
      dataType: 'VARCHAR(50)',
      fieldType: 'Required',
      examples: 'txn_123456789'
    },
    {
      name: 'customer_id',
      dataType: 'VARCHAR(50)',
      fieldType: 'Required',
      examples: 'cust_987654321'
    },
    {
      name: 'group_id',
      dataType: 'VARCHAR(50)',
      fieldType: 'Required',
      examples: 'This is the group_id in product telemetry'
    },
    {
      name: 'customer_name',
      dataType: 'VARCHAR(255)',
      fieldType: 'Required',
      examples: 'John Doe'
    },
    {
      name: 'transaction_amount',
      dataType: 'Float',
      fieldType: 'Required',
      examples: '99.99'
    },
    {
      name: 'transaction_type',
      dataType: 'ENUM',
      fieldType: 'Required',
      examples: 'payment/refund'
    },
    {
      name: 'transaction_currency',
      dataType: 'VARCHAR(3)',
      fieldType: 'Required',
      examples: 'USD, EUR, GBP | Default: USD'
    },
    {
      name: 'transaction_status',
      dataType: 'VARCHAR(50)',
      fieldType: 'Required',
      examples: 'paid, cancelled, failed, refund, charged_back'
    },
    {
      name: 'line_item_type',
      dataType: 'ENUM',
      fieldType: 'Required',
      examples: 'subscription/one_time'
    },
    {
      name: 'date',
      dataType: 'TIMESTAMP',
      fieldType: 'Required',
      examples: '2025-01-15T14:30:00Z'
    },
    {
      name: 'transaction_period_start_date',
      dataType: 'DATE',
      fieldType: 'Required',
      examples: '2025-01-15T14:30:00Z'
    },
    {
      name: 'transaction_period_end_date',
      dataType: 'DATE',
      fieldType: 'Required',
      examples: '2025-02-15T14:30:00Z'
    },
    {
      name: 'invoice_id',
      dataType: 'VARCHAR(50)',
      fieldType: 'Optional',
      examples: 'inv_112233445'
    },
    {
      name: 'payment_method',
      dataType: 'VARCHAR(50)',
      fieldType: 'Optional',
      examples: 'credit_card, paypal, stripe'
    },
    {
      name: 'tax_amount',
      dataType: 'BIGINT',
      fieldType: 'Optional',
      examples: 'Default: 0'
    },
    {
      name: 'discount_amount',
      dataType: 'BIGINT',
      fieldType: 'Optional',
      examples: 'Default: 0'
    },
    {
      name: 'transaction_fee',
      dataType: 'BIGINT',
      fieldType: 'Optional',
      examples: 'Default: 0'
    },
    {
      name: 'term_unit',
      dataType: 'ENUM',
      fieldType: 'Optional',
      examples: 'day, week, month, year | Default: month'
    },
    {
      name: 'term_frequency',
      dataType: 'INT',
      fieldType: 'Optional',
      examples: 'Default: 1'
    },
  ];

  export const invoicesSchema: SchemaField[] = [
    {
      name: 'id',
      dataType: 'VARCHAR(50)',
      fieldType: 'Required',
      examples: 'inv_123456789'
    },
    {
      name: 'customer_id',
      dataType: 'VARCHAR(50)',
      fieldType: 'Required',
      examples: 'cust_987654321'
    },
    {
      name: 'group_id',
      dataType: 'VARCHAR(50)',
      fieldType: 'Required',
      examples: 'group_001'
    },
    {
      name: 'issue_date',
      dataType: 'DATE',
      fieldType: 'Required',
      examples: '2025-01-15T14:30:00Z'
    },
    {
      name: 'invoice_amount',
      dataType: 'Float',
      fieldType: 'Required',
      examples: '299.99'
    },
    {
      name: 'currency',
      dataType: 'VARCHAR(3)',
      fieldType: 'Required',
      examples: 'USD, EUR, GBP | Default: USD'
    },
    {
      name: 'due_date',
      dataType: 'DATE',
      fieldType: 'Optional',
      examples: '2025-02-15T14:30:00Z',
      comments: 'If this data is not provided, the system will be unable to determine the applicable payment terms.'
    },
  ];

export const subscriptionsSchema: SchemaField[] = [
  {
    name: 'subscription_id',
    dataType: 'VARCHAR(50)',
    fieldType: 'Required',
    examples: 'sub_123',
    comments: 'Unique subscription identifier in your billing system.'
  },
  {
    name: 'customer_id',
    dataType: 'VARCHAR(50)',
    fieldType: 'Required',
    examples: 'cust_123',
    comments: 'Customer identifier in your billing system (can be same as group_id).'
  },
  {
    name: 'group_id',
    dataType: 'VARCHAR(50)',
    fieldType: 'Required',
    examples: 'group_456',
    comments: 'Must map to the group_id in product telemetry.'
  },
  {
    name: 'status',
    dataType: 'VARCHAR(50)',
    fieldType: 'Required',
    examples: 'active | cancelled',
    comments: 'Subscription lifecycle status.'
  },
  {
    name: 'currency',
    dataType: 'VARCHAR(3)',
    fieldType: 'Required',
    examples: 'USD, EUR, GBP',
    comments: '3-letter currency code.'
  },
  {
    name: 'start_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-01-15T00:00:00Z',
    comments: 'Subscription start timestamp (ISO 8601).'
  },
  {
    name: 'ended_at',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2025-01-15T00:00:00Z',
    comments: 'When the subscription ended. Use null if active.'
  },
  {
    name: 'trial_start_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-01-01T00:00:00Z',
    comments: 'Trial start timestamp (ISO 8601).'
  },
  {
    name: 'trial_end_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-01-14T23:59:59Z',
    comments: 'Trial end timestamp (ISO 8601).'
  },
  {
    name: 'current_period_start',
    dataType: 'TIMESTAMP',
    fieldType: 'Required',
    examples: '2024-01-15T00:00:00Z',
    comments: 'Current billing period start (required).'
  },
  {
    name: 'current_period_end',
    dataType: 'TIMESTAMP',
    fieldType: 'Required',
    examples: '2024-02-14T23:59:59Z',
    comments: 'Current billing period end (required).'
  },
  {
    name: 'cancelled_at',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: 'null | 2024-02-01T12:00:00Z',
    comments: 'When the customer cancelled. Use null if not cancelled.'
  },
  {
    name: 'cancellation_comment',
    dataType: 'VARCHAR(255)',
    fieldType: 'Optional',
    examples: 'Customer requested cancellation',
    comments: 'Reason/comment for cancellation.'
  },
  {
    name: 'create_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-01-15T00:00:00Z',
    comments: 'When the subscription record was created in your system.'
  },
  {
    name: 'updated_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-02-01T12:00:00Z',
    comments: 'When the subscription record was last updated in your system.'
  }
];

export const subscriptionItemsSchema: SchemaField[] = [
  {
    name: 'subscription_item_id',
    dataType: 'VARCHAR(50)',
    fieldType: 'Required',
    examples: 'si_123',
    comments: 'Unique subscription item identifier in your billing system.'
  },
  {
    name: 'subscription_id',
    dataType: 'VARCHAR(50)',
    fieldType: 'Required',
    examples: 'sub_123',
    comments: 'Parent subscription identifier.'
  },
  {
    name: 'customer_id',
    dataType: 'VARCHAR(50)',
    fieldType: 'Required',
    examples: 'cust_123',
    comments: 'Customer identifier in your billing system.'
  },
  {
    name: 'plan_id',
    dataType: 'VARCHAR(50)',
    fieldType: 'Optional',
    examples: 'plan_pro_monthly',
    comments: 'Plan identifier (if applicable).'
  },
  {
    name: 'price_id',
    dataType: 'VARCHAR(50)',
    fieldType: 'Optional',
    examples: 'price_123',
    comments: 'Price identifier (if applicable).'
  },
  {
    name: 'term_unit',
    dataType: 'VARCHAR(20)',
    fieldType: 'Required',
    examples: 'month | year',
    comments: 'Billing term unit.'
  },
  {
    name: 'term_frequency',
    dataType: 'VARCHAR(10)',
    fieldType: 'Required',
    examples: '1',
    comments: 'Billing term frequency (string as sent by API payload).'
  },
  {
    name: 'start_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-01-15T00:00:00Z',
    comments: 'Subscription item start timestamp.'
  },
  {
    name: 'status',
    dataType: 'VARCHAR(50)',
    fieldType: 'Required',
    examples: 'active',
    comments: 'Subscription item status.'
  },
  {
    name: 'created_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-01-15T00:00:00Z',
    comments: 'When the subscription item record was created in your system.'
  },
  {
    name: 'ended_at',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2025-01-15T00:00:00Z',
    comments: 'When the subscription item ended. Use null if active.'
  },
  {
    name: 'quantity',
    dataType: 'INT',
    fieldType: 'Required',
    examples: '1',
    comments: 'Quantity purchased (required).'
  },
  {
    name: 'trial_start_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-01-01T00:00:00Z',
    comments: 'Trial start timestamp.'
  },
  {
    name: 'trial_end_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-01-14T23:59:59Z',
    comments: 'Trial end timestamp.'
  },
  {
    name: 'cancelled_at',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: 'null | 2024-02-01T12:00:00Z',
    comments: 'When the item was cancelled. Use null if not cancelled.'
  },
  {
    name: 'current_period_start',
    dataType: 'TIMESTAMP',
    fieldType: 'Required',
    examples: '2024-01-15T00:00:00Z',
    comments: 'Current billing period start (required).'
  },
  {
    name: 'current_period_end',
    dataType: 'TIMESTAMP',
    fieldType: 'Required',
    examples: '2024-02-14T23:59:59Z',
    comments: 'Current billing period end (required).'
  },
  {
    name: 'updated_date',
    dataType: 'TIMESTAMP',
    fieldType: 'Optional',
    examples: '2024-02-01T00:00:00Z',
    comments: 'When the subscription item record was last updated in your system.'
  }
];